import { FC, ReactElement, useRef, useState } from "react";
import { Button, Box, Typography, Stack } from "@mui/material";
import { FaCaretDown } from "react-icons/fa6";

import { MenuPopover, Avatar, TenantPicker, ClientPicker } from "@/components";
import { LogoutItem } from "./logout-item";
import { useAppSelector } from "@/store";
import { useGetAllClientsQuery } from "@/services";
import { roles } from "@/roles";
import { tokens } from "@/tokens";

interface AccountPopoverProps {
  isSidebarExtended?: boolean;
  canSelectTenants?: boolean;
  canSelectClients?: boolean;
  clientPickerTenantId?: string;
  isValidateRoute?: boolean;
}

const AccountPopover: FC<AccountPopoverProps> = ({
  isSidebarExtended = true,
  canSelectTenants = false,
  canSelectClients = false,
  clientPickerTenantId = "",
  isValidateRoute = false,
}): ReactElement => {
  const user = useAppSelector((state) => state.auth.user);
  const { companyName } = useAppSelector((state) => state.settings);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useGetAllClientsQuery(
    { searchKeyword: "", pageNo: 1, pageSize: 25, tenantId: clientPickerTenantId },
    {
      skip: !canSelectClients || !clientPickerTenantId,
      refetchOnMountOrArgChange: true,
    }
  );

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  const pickerStyles = {
    width: "100%",
    border: `1px solid ${tokens.border.default}`,
    justifyContent: "flex-start",
    padding: "5px 8px",
    borderRadius: tokens.radius.md,
    fontSize: tokens.fontSize.xs,
    textTransform: "none",
    minHeight: 0,
    "& .MuiAvatar-root, & > .MuiStack-root > div:first-of-type": {
      width: "18px !important",
      height: "18px !important",
    },
    "& .MuiTypography-root": {
      fontSize: `${tokens.fontSize.xs} !important`,
    },
    "& .MuiStack-root": {
      gap: "6px !important",
    },
    "&:hover": {
      backgroundColor: tokens.bg.hover,
      borderColor: tokens.text.muted,
    },
    "&[aria-expanded='true']": {
      borderColor: tokens.brand.primary,
      boxShadow: "none",
      backgroundColor: tokens.bg.surface,
    },
  };

  const isRiffsClientAccount =
    user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT;

  const displaySubtitle =
    isRiffsClientAccount
      ? "Riffs Client"
      : selectedClient?.firstName || selectedTenant?.companyName || companyName || "No Company";

  const hasPickers = (canSelectTenants || canSelectClients) && !isValidateRoute;

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleOpen}
        fullWidth={isSidebarExtended}
        sx={{
          padding: "8px 12px",
          boxShadow: "none",
          minWidth: 0,
          justifyContent: isSidebarExtended ? "flex-start" : "center",
          height: isSidebarExtended ? 48 : "auto",
          borderRadius: tokens.radius.md,
          border: `1px solid transparent`,
          transition: 'all 150ms ease',
          '&:hover': { bgcolor: tokens.bg.hover },
          ...(open && {
            borderColor: tokens.border.default,
            bgcolor: tokens.bg.muted,
          }),
        }}
      >
        <Avatar url={user?.profileImage || undefined} width={isSidebarExtended ? 36 : 28} height={isSidebarExtended ? 36 : 28} />
        {isSidebarExtended && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1} ml={1.25} overflow="hidden">
            <Box sx={{ textAlign: "left", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography sx={{ color: tokens.text.primary, fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.sm2, lineHeight: "18px" }} noWrap>
                {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
              </Typography>
              <Typography sx={{ color: tokens.text.muted, fontWeight: tokens.fontWeight.normal, fontSize: tokens.fontSize.xs, lineHeight: "16px" }} noWrap>
                {displaySubtitle}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" ml={0.75}>
              <FaCaretDown color={tokens.text.disabled} size={11} />
            </Box>
          </Stack>
        )}
      </Button>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current as Element}
        sx={{
          width: 256,
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1.5 }}>
          {/* User info */}
          <Box sx={{
            bgcolor: tokens.bg.muted,
            borderRadius: tokens.radius.md,
            px: 1.5,
            py: 1,
          }}>
            <Typography sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.xs, color: tokens.text.primary, lineHeight: 1.4 }} noWrap>
              {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, lineHeight: 1.4 }} noWrap>
              {user?.email ?? ""}
            </Typography>
          </Box>

          {/* Pickers */}
          {hasPickers && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {canSelectTenants && (
                <Box>
                  <Typography sx={{ color: tokens.text.muted, mb: 0.25, pl: 0.25, fontWeight: tokens.fontWeight.bold, fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Tenant
                  </Typography>
                  <TenantPicker sx={pickerStyles} />
                </Box>
              )}
              {canSelectClients && (
                <Box>
                  <Typography sx={{ color: tokens.text.muted, mb: 0.25, pl: 0.25, fontWeight: tokens.fontWeight.bold, fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Client
                  </Typography>
                  <ClientPicker tenantId={clientPickerTenantId} sx={pickerStyles} />
                </Box>
              )}
            </Stack>
          )}

          {/* Divider + Logout */}
          <Box sx={{ borderTop: `1px solid ${tokens.border.default}`, mt: 1.5, pt: 0.5 }}>
            <LogoutItem navbar />
          </Box>
        </Box>
      </MenuPopover>
    </>
  );
};

export default AccountPopover;
